import { Col, Row } from 'antd';
import Icon from 'assets/Icons';
import clsx from 'clsx';
import Box from 'components/Box';
import Empty from 'components/Empty';
import ServiceItem, { IconBadge, IconCount } from 'components/ServiceItem';
import Spin from 'components/Spin';
import Text from 'components/Text';
import cashierActions from 'features/cashier/services/actions';
import cashierSelectors from 'features/cashier/services/selectors';
import { IServiceCategory } from 'features/cashier/services/types/api';
import { getServiceColorSet } from 'features/settingService/pages/SettingPage/components/ServicesSettingV2/ServicePart/PopoverChangeColors';
import { useCallback } from 'react';
import { useAppDispatch } from 'store/hooks';
import styled from 'styled-components';
import { formatCurrency } from 'utils/formatCurrency';
import ic_consent_form from 'assets/ic-consent-form.png';
const gutter = 24;
const useActiveServices = () => {
  const loading = cashierSelectors.loading.getCategories();
  const dispatch = useAppDispatch();
  const activeCate = cashierSelectors.getServicesByCateActive();
  const serviceIdsSelected = cashierSelectors.getServiceIdsSelected();

  const handleClickService = useCallback((serviceItem: IServiceCategory) => () => {
    dispatch(cashierActions.selectServiceItem(serviceItem));
  }, []);

  return ({
    loading, activeCate, serviceIdsSelected, handleClickService,
  });
};
const ActiveServices = () => {
  const { loading, activeCate, serviceIdsSelected, handleClickService } = useActiveServices();
  const renderItem = useCallback((item: IServiceCategory) => {
    const itemSelected = serviceIdsSelected.find(o => o.id === item.id);
    return (
      <ServiceItem
        size="LARGE"
        price={item.priceSell}
        service={item.serviceName}
        onClick={handleClickService(item)}
        selected={!!itemSelected}
        count={itemSelected?.count}
        colorCode={item.backgroundColor}
      />
    );
  }, [handleClickService, serviceIdsSelected]);

  const listDisplay = activeCate?.services || [];

  return (
    <>
      <ComponentStyled className='container-scroll-div'>
        <ContentContainer className='ContentContainer'>
          <Row justify='space-between' align={'middle'} style={{ flex: 1 }}>
            <Col style={{ flex: 1 }}>
              <Text variant="H6" color="text_2">
                SERVICES
              </Text>
            </Col>
          </Row>
        </ContentContainer>
        <div className='scroll-div'>
          <div className='children-scroll' style={{ marginRight: -gutter / 2, marginLeft: -gutter / 2 }}>
            <Row gutter={[gutter, gutter]} style={{ margin: 0 }}>
              {listDisplay.length > 0 ? listDisplay?.map((item: any, index: number) => {
                return (
                  <Col key={item.id + index} span={12}>
                    {renderItem(item)}
                  </Col>
                );
              }) : <Col flex={'auto'}><Empty /></Col>}
            </Row>
          </div>
        </div>
      </ComponentStyled>
      {loading && <Spin />}
    </>
  );
};

export const ActiveServicesV2 = () => {
  const { loading, activeCate, serviceIdsSelected, handleClickService } = useActiveServices();
  if (loading) return <Spin />;
  return <ActiveServicesV2Container>
    <div className="list-scroll">
      <div className="services-v2-container">
        {activeCate?.services.map(o => {
          const itemSelected = serviceIdsSelected.find(s => s.id === o.id);
          const colorSet = getServiceColorSet(o.backgroundColor);
          return (
            <ActiveServicesV2Item
              key={o.id} className={clsx(!!itemSelected && 'active')} onClick={handleClickService(o)}
              style={{ background: colorSet.bg, border: '1px solid ' + colorSet.stroke }}
            >
              <Box display='flex' alignItems='center' gap='2'>
                <span style={{ textAlign: 'left' }}>{o.serviceName}</span>
                {o.enableConsentForm && <img src={ic_consent_form} alt="ic_consent_form" style={{ width: 32, height: 32 }} />}
              </Box>
              <span className="price">{formatCurrency(o.priceSell)}</span>
              {!!itemSelected && (
                <IconBadge>
                  {itemSelected.count ? (
                    <IconCount>
                      <Text className='text'>{itemSelected.count}</Text>
                    </IconCount>
                  ) : <Icon type="tick" />}
                </IconBadge>
              )}
            </ActiveServicesV2Item>
          );
        })}
      </div>
    </div>
  </ActiveServicesV2Container>;
};
export const ActiveServicesV2Container = styled.div`
  position: relative;
  flex: 1;
  .list-scroll {
    position: absolute;
    inset: 0;
    overflow: auto;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      width: 0px;
    }
    .services-v2-container {
      display: grid;
      margin-top: 24px;
      grid-template-columns: 1fr 1fr;
      gap: 16px;
      padding-right: 24px;
      padding-bottom: 24px;
    }
  }
`;
export const ActiveServicesV2Item = styled.button`
display: flex;
height: 78px;
justify-content: space-between;
align-items: center;
flex: 1 0 0;
padding: 0 16px;
border-radius: 4px;
border: 1.5px solid #E5E6EB;
background: #E5E6EB;
position: relative;
box-shadow: 0px -4px 4px 0px rgba(0, 0, 0, 0.25) inset, 0px 8px 4px 3px rgba(255, 255, 255, 0.25) inset;
span {
  color: #1D2129;
  font-family: Poppins;
  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  &.price {
    font-weight: 600;
  }
}
&.active {
  border: 1.5px solid #FF8890;
}
`;

export default ActiveServices;
const ComponentStyled = styled.div`
  width:100%;
  &.container-scroll-div {
    flex: 1;
    display: flex;
    flex-direction: column;
    .ContentContainer {
      margin-bottom: 4px;
    }
  }
  .scroll-div {
    flex: 1;
    position: relative;
  }
  .children-scroll {
    position: absolute;
    inset: 0;
    overflow: auto;
    padding-top: 1rem;
    padding-bottom: 1.5rem;
    padding-right: 4px;
    scrollbar-width: none; /* Firefox 64 */
    &::-webkit-scrollbar {
      width: 0px;
      height: 8px;
      background-color: #F5F5F5;
    }
  
    &::-webkit-scrollbar-thumb {
      border-radius: 4px;
      background-color: rgba(0,0,0,0.5);
      &:hover {
        background-color: rgba(0,0,0,0.6);
      }
    }
  
    &::-webkit-scrollbar-track {
      border-radius: 4px;
      background-color: #F5F5F5;
    }
  }
`;

const ContentContainer = styled.div`
  margin-bottom: 1rem;
`;